import { FC } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonList,
  Checkbox,
  Fieldset,
  Input,
  Text,
} from "@postenbring/hedwig-react";
import Translated from "../infrastructure/translation/Translated";
import {
  AccountsWithNameByApplication,
  CertificateRepresentation,
} from "../infrastructure/api/api";
import { useApplications } from "../infrastructure/entrypoint/EntrypointContext";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { scopeOverview } from "./scopeUtils";
import { ClientFormState } from "./useClientForm";
import styles from "./ClientForm.module.css";

interface ClientFormProps {
  form: ClientFormState;
  availableAccounts: AccountsWithNameByApplication;
  submitTextKey: "new_client.submit" | "edit_client.submit";
  onCancel: () => void;
  certificates?: CertificateRepresentation[];
  unusableCertificateCount?: number;
}

export const ClientForm: FC<ClientFormProps> = ({
  form,
  availableAccounts,
  submitTextKey,
  onCancel,
  certificates,
  unusableCertificateCount = 0,
}) => {
  const translate = useTranslate();
  const applications = scopeOverview(
    useApplications(),
    availableAccounts,
    form.selectedScopes,
  );
  const missingCertificate =
    certificates && form.selectedCertificateIds.length === 0;
  const selectedCount = Object.values(form.selectedScopes).reduce(
    (total, accounts) => total + accounts.length,
    0,
  );

  return (
    <Box variant="white" asChild>
      <form onSubmit={form.onSubmit} className={styles.form}>
        <div className={styles.field}>
          <Input
            label={translate("client.display_name")}
            value={form.displayName}
            onChange={(e) => form.setDisplayName(e.target.value)}
            required
          />
          <Text variant="body-small" className={styles.help}>
            <Translated textKey={"new_client.display_name_help"} />
          </Text>
        </div>

        {certificates && (
          <Fieldset className={styles.scopes}>
            <Fieldset.Legend className={styles.scopesLegend}>
              <Text variant="h3" as="span" size="min">
                <Translated textKey={"new_client.select_certificates"} />
              </Text>
              <span className={styles.selectedCount}>
                <Translated
                  textKey={"new_client.certificates_selected_count"}
                  params={{ count: form.selectedCertificateIds.length }}
                />
              </span>
            </Fieldset.Legend>

            <Text variant="body-small" className={styles.help}>
              <Translated textKey={"new_client.select_certificates_help"} />
            </Text>

            {unusableCertificateCount > 0 && (
              <Alert
                data-color="warning"
                role="alert"
                className={styles.certificateWarning}
              >
                <Alert.Description>
                  <Translated
                    textKey={"edit_client.unusable_certificates_removed"}
                    params={{ count: unusableCertificateCount }}
                  />
                </Alert.Description>
              </Alert>
            )}

            {certificates.length === 0 ? (
              <p className={styles.noScopes}>
                <Translated textKey={"new_client.no_certificates_available"} />
              </p>
            ) : (
              <div className={styles.scopeGroup}>
                {certificates.map((cert) => (
                  <div key={cert.certificate.id} className={styles.scopeRow}>
                    <Checkbox
                      checked={form.selectedCertificateIds.includes(
                        cert.certificate.id,
                      )}
                      onChange={(e) =>
                        form.onCertificateChange(
                          cert.certificate.id,
                          e.target.checked,
                        )
                      }
                    >
                      {cert.certificate.displayName}
                    </Checkbox>
                  </div>
                ))}
              </div>
            )}
          </Fieldset>
        )}

        <Fieldset className={styles.scopes}>
          <Fieldset.Legend className={styles.scopesLegend}>
            <Text variant="h3" as="span" size="min">
              <Translated textKey={"new_client.select_scopes"} />
            </Text>
            <span className={styles.selectedCount}>
              <Translated
                textKey={"client.scopes_selected_count"}
                params={{ count: selectedCount }}
              />
            </span>
          </Fieldset.Legend>

          {applications.length === 0 ? (
            <p className={styles.noScopes}>
              <Translated textKey={"new_client.no_scopes_available"} />
            </p>
          ) : (
            applications.map((application) => (
              <div key={application.oAuthId} className={styles.scopeGroup}>
                <div className={styles.scopeGroupHeader}>
                  <span>{application.displayName}</span>
                  <span className={styles.rule} />
                </div>
                {application.accounts.map((account) => (
                  <div key={account.accountId} className={styles.scopeRow}>
                    <Checkbox
                      checked={account.granted}
                      onChange={(e) =>
                        form.onScopeChange(
                          application.oAuthId,
                          account.accountId,
                          e.target.checked,
                        )
                      }
                    >
                      {account.name ?? translate("client.account_id")}
                    </Checkbox>
                    <span className={styles.idChip}>
                      id: {account.accountId}
                    </span>
                  </div>
                ))}
              </div>
            ))
          )}
        </Fieldset>

        {form.error && <p className={styles.error}>{form.error}</p>}

        <ButtonList className={styles.actions}>
          <Button
            size="small"
            type="submit"
            disabled={!form.hasSelectedScopes || missingCertificate}
          >
            <Translated textKey={submitTextKey} />
          </Button>
          <Button
            size="small"
            variant="secondary"
            type="button"
            onClick={onCancel}
          >
            <Translated textKey={"generic.cancel"} />
          </Button>
        </ButtonList>
      </form>
    </Box>
  );
};
